import { create } from 'zustand';
import { getApiUrl } from "@/utils/apiConfig";

interface PartnerAccessSettings {
  isRestrictionEnabled: boolean;
  partyLimit: number;
  mediaLimit: number;
  ongLimit: number;
  institutionLimit: number;
}

interface PartnerAccessStore {
  settings: PartnerAccessSettings;
  isLoaded: boolean;
  fetchSettings: () => Promise<void>;
  setRestrictionEnabled: (enabled: boolean) => Promise<void>;
  setPartyLimit: (limit: number) => Promise<void>;
  setMediaLimit: (limit: number) => Promise<void>;
  setOngLimit: (limit: number) => Promise<void>;
  setInstitutionLimit: (limit: number) => Promise<void>;
  saveSettings: (newSettings: PartnerAccessSettings) => Promise<void>;
}

const API_URL = getApiUrl();

export const usePartnerAccessStore = create<PartnerAccessStore>((set, get) => ({
  settings: {
    isRestrictionEnabled: false,
    partyLimit: 1,
    mediaLimit: 3,
    ongLimit: 2,
    institutionLimit: 2,
  },
  isLoaded: false,

  fetchSettings: async () => {
    try {
      const res = await fetch(`${API_URL}/settings.php?t=${Date.now()}`);
      if (!res.ok) return;
      const data = await res.json();
      
      const isRestrictionEnabled = data.kaynin_partner_restriction === 'true';
      const partyLimit = parseInt(data.kaynin_partner_party_limit || '1', 10);
      const mediaLimit = parseInt(data.kaynin_partner_media_limit || '3', 10);
      const ongLimit = parseInt(data.kaynin_partner_ong_limit || '2', 10);
      const institutionLimit = parseInt(data.kaynin_partner_inst_limit || '2', 10);

      set({
        settings: { isRestrictionEnabled, partyLimit, mediaLimit, ongLimit, institutionLimit },
        isLoaded: true
      });
    } catch (e) {
      console.error("Failed to load partner settings", e);
    }
  },

  saveSettings: async (newSettings: PartnerAccessSettings) => {
    try {
      const token = localStorage.getItem('kaynin_auth_token');
      const payload = {
        action: 'update_all',
        settings: {
          kaynin_partner_restriction: newSettings.isRestrictionEnabled ? 'true' : 'false',
          kaynin_partner_party_limit: newSettings.partyLimit.toString(),
          kaynin_partner_media_limit: newSettings.mediaLimit.toString(),
          kaynin_partner_ong_limit: newSettings.ongLimit.toString(),
          kaynin_partner_inst_limit: newSettings.institutionLimit.toString()
        }
      };

      const res = await fetch(`${API_URL}/settings.php`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        set({ settings: newSettings });
      } else {
        throw new Error("Failed to save to server");
      }
    } catch (e) {
      console.error("Failed to save partner settings", e);
      alert("Erreur de sauvegarde des paramètres partenaires sur le serveur.");
    }
  },

  setRestrictionEnabled: async (enabled) => {
    const newSettings = { ...get().settings, isRestrictionEnabled: enabled };
    await get().saveSettings(newSettings);
  },
  setPartyLimit: async (limit) => {
    const newSettings = { ...get().settings, partyLimit: limit };
    await get().saveSettings(newSettings);
  },
  setMediaLimit: async (limit) => {
    const newSettings = { ...get().settings, mediaLimit: limit };
    await get().saveSettings(newSettings);
  },
  setOngLimit: async (limit) => {
    const newSettings = { ...get().settings, ongLimit: limit };
    await get().saveSettings(newSettings);
  },
  setInstitutionLimit: async (limit) => {
    const newSettings = { ...get().settings, institutionLimit: limit };
    await get().saveSettings(newSettings);
  },
}));

if (typeof window !== "undefined") {
  usePartnerAccessStore.getState().fetchSettings();
}

export const getPartnerAccessSettings = () => usePartnerAccessStore.getState().settings;
